import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import type { CardLayout } from '../types'

// A small, dependency-free snap grid: absolute-positioned cards on a fixed
// column grid, drag-to-move (via a .drag-handle inside the card) and
// resize-from-corner, both snapped to cells. Vertical compaction keeps it tidy
// and overlap-free. We own the pointer handling end-to-end so it just works on
// React 19.

export interface GridItem {
  id: string
  layout: CardLayout
  minW: number
  minH: number
}

interface DragState {
  id: string
  mode: 'move' | 'resize'
  startPx: number
  startPy: number
  orig: CardLayout
  preview: CardLayout
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

function overlaps(a: CardLayout, b: CardLayout): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
}

// Vertical compaction: keep each card's column, float it as high as it fits.
function compact(items: GridItem[]): GridItem[] {
  const sorted = [...items].sort((p, q) => p.layout.y - q.layout.y || p.layout.x - q.layout.x)
  const placed: GridItem[] = []
  for (const it of sorted) {
    const cand: CardLayout = { ...it.layout, y: 0 }
    while (placed.some((p) => overlaps(cand, p.layout))) cand.y++
    placed.push({ ...it, layout: cand })
  }
  return placed
}

export function SnapGrid({
  items,
  cols = 12,
  rowHeight = 44,
  gap = 16,
  renderItem,
  onChange,
}: {
  items: GridItem[]
  cols?: number
  rowHeight?: number
  gap?: number
  renderItem: (id: string) => ReactNode
  onChange: (layouts: Record<string, CardLayout>) => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  const [drag, setDrag] = useState<DragState | null>(null)
  // Keep the desktop arrangement stored; render readable full-width cards on
  // narrow screens without overwriting the user's saved column positions.
  // Three default columns need about 300 px per chart. Tablets (and a narrow
  // workspace beside the sidebar) otherwise leave horizontal plots unreadable.
  const stacked = width > 0 && width < 900

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width))
    ro.observe(el)
    setWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  const unitW = width > 0 ? (width + gap) / cols : 0
  const unitH = rowHeight + gap

  // Live items: during a drag, replace the dragged card's layout with its preview.
  const live = drag ? items.map((it) => (it.id === drag.id ? { ...it, layout: drag.preview } : it)) : items
  const rows = live.reduce((m, it) => Math.max(m, it.layout.y + it.layout.h), 0)
  const height = rows * unitH

  useEffect(() => {
    if (!drag) return
    const onMove = (e: PointerEvent) => {
      const dCols = unitW > 0 ? Math.round((e.clientX - drag.startPx) / unitW) : 0
      const dRows = Math.round((e.clientY - drag.startPy) / unitH)
      const o = drag.orig
      const it = items.find((i) => i.id === drag.id)
      const minW = it?.minW ?? 2
      const minH = it?.minH ?? 2
      let preview: CardLayout
      if (drag.mode === 'move') {
        preview = { ...o, x: clamp(o.x + dCols, 0, cols - o.w), y: Math.max(0, o.y + dRows) }
      } else {
        preview = {
          ...o,
          w: clamp(o.w + dCols, minW, cols - o.x),
          h: Math.max(minH, o.h + dRows),
        }
      }
      setDrag((d) => (d ? { ...d, preview } : d))
    }
    const onUp = () => {
      const finalItems = items.map((it) => (it.id === drag.id ? { ...it, layout: drag.preview } : it))
      const compacted = compact(finalItems)
      const map: Record<string, CardLayout> = {}
      for (const it of compacted) map[it.id] = it.layout
      onChange(map)
      setDrag(null)
    }
    const onCancel = () => setDrag(null)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
    }
  }, [drag, items, unitW, unitH, cols, onChange])

  const begin = (mode: 'move' | 'resize', it: GridItem, e: React.PointerEvent) => {
    if (stacked || e.button !== 0) return
    e.preventDefault()
    setDrag({ id: it.id, mode, startPx: e.clientX, startPy: e.clientY, orig: it.layout, preview: it.layout })
  }

  const keyboard = (it: GridItem, e: React.KeyboardEvent, resize: boolean) => {
    if (stacked || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return
    e.preventDefault()
    const dx = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
    const dy = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0
    const o = it.layout
    const layout = resize
      ? { ...o, w: clamp(o.w + dx, it.minW, cols - o.x), h: Math.max(it.minH, o.h + dy) }
      : { ...o, x: clamp(o.x + dx, 0, cols - o.w), y: Math.max(0, o.y + dy) }
    const next = compact(items.map((item) => item.id === it.id ? { ...item, layout } : item))
    onChange(Object.fromEntries(next.map((item) => [item.id, item.layout])))
  }

  return (
    <div
      ref={ref}
      className={`snap-grid ${drag ? 'dragging' : ''} ${stacked ? 'stacked' : ''}`}
      style={{ position: 'relative', height: stacked ? undefined : height || undefined, ...(stacked ? { display: 'grid', gap } : {}) }}
    >
      {width > 0 &&
        live.map((it) => {
          const box: React.CSSProperties = {
            position: stacked ? 'relative' : 'absolute',
            left: stacked ? undefined : it.layout.x * unitW,
            top: stacked ? undefined : it.layout.y * unitH,
            width: stacked ? '100%' : it.layout.w * unitW - gap,
            height: it.layout.h * unitH - gap,
          }
          return (
            <div
              key={it.id}
              className={`snap-item ${drag?.id === it.id ? 'active' : ''}`}
              style={box}
              onKeyDown={(e) => {
                if ((e.target as HTMLElement).closest('.drag-handle')) keyboard(it, e, false)
              }}
              onPointerDown={(e) => {
                if ((e.target as HTMLElement).closest('.drag-handle')) begin('move', it, e)
              }}
            >
              {renderItem(it.id)}
              {!stacked && <button
                className="snap-resize"
                aria-label="Resize card with arrow keys"
                title="Resize card · drag or use arrow keys"
                onKeyDown={(e) => keyboard(it, e, true)}
                onPointerDown={(e) => {
                  e.stopPropagation()
                  begin('resize', it, e)
                }}
              />}
            </div>
          )
        })}
    </div>
  )
}
